import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { motion, useScroll, useTransform } from 'framer-motion';
import { analytics } from '../../services/analytics';

// ── Dashboard phone mockup ───────────────────────────────────────────────────

function DashboardMockup() {
  return (
    <div className="relative select-none" aria-hidden="true">
      {/* Ambient glow behind phone */}
      <div
        className="absolute pointer-events-none blur-3xl rounded-full opacity-30"
        style={{
          inset: '-3rem',
          background: 'radial-gradient(ellipse, rgba(79,137,132,0.55) 0%, transparent 65%)',
        }}
      />

      {/* Primary phone */}
      <motion.div
        initial={{ opacity: 0, y: 32 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, delay: 0.35, ease: [0.22, 1, 0.36, 1] as const }}
        className="relative rounded-[44px] overflow-hidden"
        style={{
          width: '248px',
          height: '504px',
          border: '1.5px solid rgba(255,255,255,0.13)',
          background: '#0b101c',
          boxShadow:
            '0 48px 96px rgba(0,0,0,0.72), 0 0 0 1px rgba(255,255,255,0.04), inset 0 1px 0 rgba(255,255,255,0.08)',
          transform: 'rotate(2deg)',
        }}
      >
        {/* Dynamic island */}
        <div className="absolute top-3 left-1/2 -translate-x-1/2 w-20 h-6 rounded-full bg-black z-20" />

        {/* Status bar */}
        <div className="flex justify-between items-center px-5 pt-11 pb-2 text-[9px] text-white/30 font-medium">
          <span>9:41</span>
          <span>●●● ▮▮▮</span>
        </div>

        {/* App content */}
        <div className="px-3.5 space-y-2 overflow-hidden">
          {/* Welcome row */}
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[8px] text-white/30">Monday · Feb 23</p>
              <p className="text-[11px] font-semibold text-white">Good morning ☀️</p>
            </div>
            <div
              className="w-7 h-7 rounded-full flex items-center justify-center text-[9px] font-bold text-teal-300"
              style={{
                background: 'rgba(79,137,132,0.18)',
                border: '1px solid rgba(79,137,132,0.3)',
              }}
            >
              MG
            </div>
          </div>

          {/* Sobriety card */}
          <div
            className="rounded-2xl p-3"
            style={{
              background:
                'linear-gradient(135deg, rgba(79,137,132,0.16), rgba(47,57,87,0.25))',
              border: '1px solid rgba(79,137,132,0.22)',
            }}
          >
            <p className="text-[7px] text-teal-400/70 font-semibold uppercase tracking-widest mb-0.5">
              Alcoholics Anonymous
            </p>
            <div className="flex items-baseline gap-1.5">
              <span className="text-3xl font-bold text-white leading-none">247</span>
              <span className="text-xs text-teal-400 font-medium">days</span>
            </div>
            <p className="text-[7px] text-white/25 mt-0.5">8 months · 1 week · 2 days</p>
            <div className="mt-1.5 flex gap-0.5">
              {[7, 30, 60, 90, 180, 247].map((_, i) => (
                <div
                  key={i}
                  className="h-1 flex-1 rounded-full"
                  style={{ background: i < 5 ? '#4f8984' : 'rgba(79,137,132,0.25)' }}
                />
              ))}
            </div>
          </div>

          {/* Check-in grid */}
          <div className="grid grid-cols-2 gap-1.5">
            {[
              { emoji: '☀️', label: 'Morning', done: true },
              { emoji: '🌙', label: 'Nightly', done: false },
            ].map((item) => (
              <div
                key={item.label}
                className="rounded-xl p-2.5"
                style={{
                  background: 'rgba(255,255,255,0.04)',
                  border: `1px solid ${item.done ? 'rgba(79,137,132,0.3)' : 'rgba(255,255,255,0.07)'}`,
                }}
              >
                <span className="text-sm">{item.emoji}</span>
                <p className="text-[9px] font-semibold text-white mt-1">{item.label}</p>
                <p
                  className="text-[7px] font-medium"
                  style={{ color: item.done ? '#4abbb5' : 'rgba(255,255,255,0.28)' }}
                >
                  {item.done ? '✓ Complete' : 'Check-in'}
                </p>
              </div>
            ))}
          </div>

          {/* Step progress */}
          <div
            className="rounded-xl p-2.5"
            style={{
              background: 'rgba(255,255,255,0.035)',
              border: '1px solid rgba(255,255,255,0.07)',
            }}
          >
            <div className="flex justify-between items-center mb-1.5">
              <p className="text-[9px] font-semibold text-white">Step Work</p>
              <span
                className="text-[7px] font-semibold px-1.5 py-0.5 rounded-full"
                style={{
                  background: 'rgba(79,137,132,0.18)',
                  color: 'rgb(100,200,195)',
                }}
              >
                Step 5 →
              </span>
            </div>
            <div className="flex gap-0.5">
              {Array.from({ length: 12 }, (_, i) => (
                <div
                  key={i}
                  className="h-1 flex-1 rounded-full"
                  style={{
                    background:
                      i < 4
                        ? '#4f8984'
                        : i === 4
                        ? 'rgba(79,137,132,0.38)'
                        : 'rgba(255,255,255,0.07)',
                  }}
                />
              ))}
            </div>
          </div>

          {/* Reflection teaser */}
          <div
            className="rounded-xl p-2.5"
            style={{
              background: 'rgba(255,255,255,0.025)',
              border: '1px solid rgba(255,255,255,0.055)',
            }}
          >
            <p className="text-[7px] text-white/25 uppercase tracking-wider mb-1">
              Daily Reflection
            </p>
            <p className="text-[8px] text-white/55 italic leading-relaxed">
              &ldquo;Faith without works is dead&hellip;&rdquo;
            </p>
          </div>
        </div>
      </motion.div>

      {/* Secondary phone peeking behind */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.75 }}
        className="absolute -right-9 top-6 -z-10 rounded-[40px]"
        style={{
          width: '220px',
          height: '440px',
          background: '#111827',
          border: '1.5px solid rgba(255,255,255,0.06)',
          boxShadow: '0 24px 56px rgba(0,0,0,0.5)',
          transform: 'rotate(7deg)',
        }}
        aria-hidden="true"
      />
    </div>
  );
}

// ── Hero section ─────────────────────────────────────────────────────────────

export function HeroSection() {
  const sectionRef = useRef<HTMLElement>(null);

  // Parallax: mountains move up more slowly than page content as user scrolls
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start start', 'end start'],
  });
  const mountainsY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section
      ref={sectionRef}
      className="relative min-h-screen flex flex-col pt-16 overflow-hidden bg-[#0D1321]"
    >
      {/* Left-biased radial glow */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse 65% 55% at 28% 42%, rgba(65,90,119,0.32) 0%, transparent 70%)',
        }}
        aria-hidden="true"
      />

      {/* Dot-grid texture */}
      <div
        className="absolute inset-0 pointer-events-none opacity-20"
        style={{
          backgroundImage:
            'radial-gradient(circle, rgba(119,141,169,0.22) 1px, transparent 1px)',
          backgroundSize: '32px 32px',
        }}
        aria-hidden="true"
      />

      {/* Mountains backdrop — parallax, anchored to bottom */}
      <motion.div
        className="absolute bottom-0 left-0 right-0 pointer-events-none overflow-hidden"
        style={{ height: '54%', y: mountainsY }}
        aria-hidden="true"
      >
        {/* Gradient fade: page colour → transparent → mountains visible */}
        <div
          className="absolute inset-x-0 top-0 z-10"
          style={{
            height: '58%',
            background: 'linear-gradient(to bottom, #0D1321 0%, transparent 100%)',
          }}
        />
        <img
          src="/assets/mountains.svg"
          alt=""
          draggable="false"
          className="absolute bottom-0 left-0 w-full"
          style={{ height: '100%', objectFit: 'cover', objectPosition: 'center bottom' }}
        />
      </motion.div>

      {/* Two-column content */}
      <div className="relative z-10 flex-1 flex items-center">
        <div className="max-w-6xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-16 lg:py-0">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">

            {/* ── Left: logo + copy + CTAs ── */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] as const }}
              className="flex flex-col items-start text-left"
            >
              {/* Logo — large and prominent */}
              <motion.img
                src="/assets/logo/daily-reprieve-full.webp"
                alt="Daily Reprieve"
                className="mb-7 w-auto"
                style={{ height: '120px' }}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] as const }}
              />

              {/* Chip */}
              <div
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-5 text-xs font-medium text-teal-300"
                style={{
                  background: 'rgba(79,137,132,0.12)',
                  border: '1px solid rgba(79,137,132,0.3)',
                }}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-teal-400 animate-pulse" />
                Free · Private · No credit card required
              </div>

              {/* Headline */}
              <h1 className="text-4xl sm:text-5xl font-bold text-white leading-tight mb-5 tracking-tight">
                Your recovery,
                <br />
                <span
                  className="text-teal-400"
                  style={{ textShadow: '0 0 56px rgba(79,160,155,0.45)' }}
                >
                  one day at a time.
                </span>
              </h1>

              {/* Subheadline */}
              <p className="text-lg text-slate-400 max-w-lg mb-8 leading-relaxed">
                A free, private companion for anyone in 12-step recovery. Morning check-ins,
                nightly reviews, all 12 steps, and your sponsor — in one place.
              </p>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-3 mb-8">
                <Link
                  to="/register"
                  onClick={() => analytics.landingCtaClicked("Get Started — It's Free", 'hero')}
                  className="inline-flex items-center justify-center px-7 py-3.5 rounded-xl bg-teal-600 hover:bg-teal-500 active:bg-teal-700 text-white font-semibold text-base transition-all duration-200"
                  style={{ boxShadow: '0 4px 24px rgba(65,137,132,0.45)' }}
                >
                  Get Started — It&apos;s Free
                </Link>
                <Link
                  to="/login"
                  className="inline-flex items-center justify-center px-7 py-3.5 rounded-xl text-slate-300 hover:text-white font-semibold text-base transition-all duration-200"
                  style={{
                    border: '1px solid rgba(255,255,255,0.13)',
                    background: 'rgba(255,255,255,0.04)',
                  }}
                >
                  Sign In
                </Link>
              </div>

              {/* Social proof */}
              <p className="text-xs text-slate-500">
                Built on the principles of the Big Book · All 12 steps · 24 fellowships
              </p>
            </motion.div>

            {/* ── Right: phone mockup ── */}
            <div className="hidden lg:flex justify-center lg:justify-end items-center">
              <DashboardMockup />
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 text-white/20 animate-bounce">
        <ChevronDownIcon className="w-5 h-5" />
      </div>
    </section>
  );
}
